<template>
  <div class="sitemap-container">
    <div class="sitemap-header">
      <h1>{{ t('message.sitemap') }}</h1>
      <p class="subtitle">{{ t('message.sitemapSubtitle') }}</p>
    </div>

    <div class="sitemap-content" v-if="!loading">
      <!-- 静态页面 -->
      <div class="sitemap-section">
        <h2 class="section-title">
          <span class="icon">📄</span>
          {{ t('message.staticPages') }}
        </h2>
        <ul class="url-list">
          <li v-for="page in sitemapData.staticPages" :key="page.url" class="url-item">
            <a :href="page.url" class="url-link">
              <span class="page-title">{{ page.title }}</span>
              <span class="url-text">{{ page.url }}</span>
            </a>
            <span class="meta-info">
              <span class="priority" :class="'priority-' + Math.floor(page.priority * 10)">
                {{ t('message.priority') }}: {{ page.priority }}
              </span>
              <span class="changefreq">
                {{ t('message.updateFrequency') }}: {{ t('message.' + page.changefreq) }}
              </span>
            </span>
          </li>
        </ul>
      </div>

      <!-- 产品分类 -->
      <div class="sitemap-section">
        <h2 class="section-title">
          <span class="icon">📦</span>
          {{ t('message.productCategories') }}
        </h2>
        <ul class="url-list">
          <li v-for="category in sitemapData.productCategories" :key="category.name" class="url-item">
            <a :href="category.url" class="url-link">
              <span class="page-title">{{ category.displayName }}</span>
              <span class="url-text">{{ category.name }}</span>
            </a>
            <span class="meta-info">
              <span class="product-count">{{ category.count }} {{ t('message.productUnit') }}</span>
              <span class="priority priority-9">
                {{ t('message.priority') }}: 0.9
              </span>
            </span>
          </li>
        </ul>
      </div>

      <!-- 产品列表（前100个） -->
      <div class="sitemap-section" v-if="sitemapData.products.length > 0">
        <h2 class="section-title">
          <span class="icon">🔧</span>
          {{ t('message.productList') }}
          <span class="count-badge">({{ displayedProducts.length }})</span>
        </h2>
        
        <div class="filter-bar">
          <select v-model="selectedCategory" @change="filterProducts" class="category-filter">
            <option value="">{{ t('message.allCategories') }}</option>
            <option v-for="category in sitemapData.productCategories" :key="category.name" :value="category.name">
              {{ category.displayName }}
            </option>
          </select>
          <input 
            v-model="searchQuery" 
            @input="filterProducts"
            :placeholder="t('message.searchProducts')" 
            class="search-input"
          />
        </div>

        <ul class="url-list product-list">
          <li v-for="product in displayedProducts.slice(0, showCount)" :key="product.url" class="url-item product-item">
            <a :href="product.url" class="url-link">
              <span class="page-title">{{ product.title }}</span>
              <span class="url-text">{{ getCategoryName(product.category) }}</span>
            </a>
          </li>
        </ul>

        <div class="load-more" v-if="displayedProducts.length > showCount">
          <button @click="showMore" class="load-more-btn">
            {{ t('message.loadMore') }} ({{ displayedProducts.length - showCount }})
          </button>
        </div>
      </div>

      <!-- 网站地图链接 -->
      <div class="sitemap-section">
        <h2 class="section-title">
          <span class="icon">🗺️</span>
          {{ t('message.xmlSitemap') }}
        </h2>
        <div class="xml-sitemap-link">
          <a href="/sitemap.xml" target="_blank" class="xml-link">
            📄 sitemap.xml
          </a>
          <span class="description">{{ t('message.xmlSitemapDescription') }}</span>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div class="loading" v-if="loading">
      <div class="spinner"></div>
      <p>{{ t('message.loading') }}</p>
    </div>

    <!-- 错误状态 -->
    <div class="error" v-if="error">
      <p>{{ error }}</p>
      <button @click="fetchSitemap" class="retry-btn">{{ t('message.retry') }}</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../api.js'

export default {
  name: 'SiteMap',
  setup() {
    const { t } = useI18n()
    const loading = ref(true)
    const error = ref('')
    const sitemapData = ref({
      staticPages: [],
      productCategories: [],
      products: []
    })
    const searchQuery = ref('')
    const selectedCategory = ref('')
    const showCount = ref(50)

    const categoryNames = computed(() => {
      const names = {}
      sitemapData.value.productCategories.forEach(cat => {
        names[cat.name] = cat.displayName
      })
      return names
    })

    const displayedProducts = computed(() => {
      let products = sitemapData.value.products

      // 按分类过滤
      if (selectedCategory.value) {
        products = products.filter(p => p.category === selectedCategory.value)
      }

      // 按搜索词过滤
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        products = products.filter(p => 
          p.title.toLowerCase().includes(query)
        )
      }

      return products
    })

    const getCategoryName = (categoryKey) => {
      return categoryNames.value[categoryKey] || categoryKey
    }

    const fetchSitemap = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const response = await api.get('/api/sitemap')
        if (response.data && response.data.success) {
          sitemapData.value = response.data.data
        } else {
          // API 返回错误，使用静态数据
          useStaticData()
        }
      } catch (err) {
        console.error('获取网站地图失败，使用静态数据:', err)
        // API 不可用时使用静态数据
        useStaticData()
      } finally {
        loading.value = false
      }
    }

    // 静态数据后备
    const useStaticData = () => {
      const currentDate = new Date().toISOString().split('T')[0]
      sitemapData.value = {
        staticPages: [
          { url: 'https://765.info/', title: '首页', priority: '1.0', changefreq: 'daily', lastmod: currentDate },
          { url: 'https://765.info/#about', title: '关于我们', priority: '0.8', changefreq: 'weekly', lastmod: currentDate },
          { url: 'https://765.info/#contact', title: '联系我们', priority: '0.8', changefreq: 'weekly', lastmod: currentDate }
        ],
        productCategories: [
          { name: 'xfjMosfet', displayName: '肖特基二极管', count: 0, url: 'https://765.info/#xfjMosfet' },
          { name: 'cgjMosfet', displayName: '超结MOSFET', count: 0, url: 'https://765.info/#cgjMosfet' },
          { name: 'gyjMosfet', displayName: '高压MOSFET', count: 0, url: 'https://765.info/#gyjMosfet' },
          { name: 'powermodule', displayName: '功率模块', count: 0, url: 'https://765.info/#powermodule' },
          { name: 'dcdc', displayName: 'DC-DC转换器', count: 0, url: 'https://765.info/#dcdc' }
        ],
        products: []
      }
    }

    const filterProducts = () => {
      showCount.value = 50
    }

    const showMore = () => {
      showCount.value += 50
    }

    onMounted(() => {
      fetchSitemap()
    })

    return {
      t,
      loading,
      error,
      sitemapData,
      searchQuery,
      selectedCategory,
      showCount,
      displayedProducts,
      getCategoryName,
      filterProducts,
      showMore,
      fetchSitemap
    }
  }
}
</script>

<style scoped>
.sitemap-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.sitemap-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  color: white;
}

.sitemap-header h1 {
  font-size: 2.5em;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 1.1em;
  opacity: 0.9;
  margin: 0;
}

.sitemap-section {
  background: white;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.4em;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #42b983;
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon {
  font-size: 1.2em;
}

.count-badge {
  font-size: 0.8em;
  color: #666;
  font-weight: normal;
  margin-left: 10px;
}

.url-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.url-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 15px 0;
}

.url-item:last-child {
  border-bottom: none;
}

.url-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
}

.url-link:hover {
  background-color: #f8f9fa;
  margin: 0 -10px;
  padding: 15px 10px;
  border-radius: 4px;
}

.page-title {
  font-weight: 600;
  color: #2c3e50;
  flex: 1;
}

.url-text {
  color: #999;
  font-size: 0.9em;
  margin-left: 20px;
  font-family: 'Courier New', monospace;
}

.meta-info {
  display: flex;
  gap: 15px;
  margin-top: 8px;
  font-size: 0.85em;
  color: #666;
}

.priority {
  padding: 2px 8px;
  border-radius: 3px;
  font-weight: bold;
}

.priority-10 {
  background-color: #d4edda;
  color: #155724;
}

.priority-9 {
  background-color: #cce5ff;
  color: #004085;
}

.priority-8 {
  background-color: #e2e3e5;
  color: #383d41;
}

.changefreq,
.product-count {
  background-color: #f8f9fa;
  padding: 2px 8px;
  border-radius: 3px;
}

.filter-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.category-filter,
.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.category-filter {
  min-width: 200px;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.product-list .url-item {
  padding: 12px 0;
}

.load-more {
  text-align: center;
  margin-top: 20px;
}

.load-more-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
}

.load-more-btn:hover {
  transform: scale(1.05);
}

.xml-sitemap-link {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.xml-link {
  color: #42b983;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1em;
  padding: 10px 20px;
  background-color: white;
  border-radius: 4px;
  border: 2px solid #42b983;
  transition: all 0.3s ease;
}

.xml-link:hover {
  background-color: #42b983;
  color: white;
}

.description {
  color: #666;
  font-size: 0.95em;
}

.loading {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 60px 20px;
  color: #dc3545;
}

.retry-btn {
  margin-top: 20px;
  background-color: #42b983;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.retry-btn:hover {
  background-color: #3aa876;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .sitemap-header h1 {
    font-size: 1.8em;
  }

  .url-link {
    flex-direction: column;
    align-items: flex-start;
  }

  .url-text {
    margin-left: 0;
    margin-top: 5px;
  }

  .meta-info {
    flex-direction: column;
    gap: 5px;
  }

  .filter-bar {
    flex-direction: column;
  }

  .category-filter,
  .search-input {
    width: 100%;
  }

  .xml-sitemap-link {
    flex-direction: column;
    text-align: center;
  }
}
</style>
